import { Component, OnInit, OnDestroy, computed, inject, signal } from '@angular/core';
import { NgClass, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';

import { ImageSyncService } from '../../../../services/image-sync/image-sync.service';
import { ConfigService } from '../../../../services/config/config.service';
import { LoggingService } from '../../../../services/shared/logging/logging.service';
import { DialogService } from '../../../../services/shared/dialog/dialog.service';
import { AlertContainerComponent } from '../../../../components/shared/alert-container/alert-container.component';
import { TablePaginationComponent, PageChangeEvent } from '../../../../components/shared/table-pagination/table-pagination.component';
import { PaginationResult } from '../../../../services/shared/table-pagination/table-pagination.service';

import {
  SyncStatusResult,
  SyncHistoryResult,
  SyncHistoryItem,
  ReconcileSyncResult
} from '../../../../models/image-sync.models';

/**
 * Component for monitoring and managing the Dropbox→S3 image synchronization pipeline.
 * Consumes the R2IT.Golf.Scoring.Images API.
 */
@Component({
  selector: 'app-image-sync',
  standalone: true,
  imports: [
    NgClass,
    DecimalPipe,
    FormsModule,
    AlertContainerComponent,
    TablePaginationComponent
  ],
  templateUrl: './image-sync.component.html',
  styleUrls: ['./image-sync.component.scss']
})
export class ImageSyncComponent implements OnInit, OnDestroy {
  private imageSyncService = inject(ImageSyncService);
  private configService = inject(ConfigService);
  private dialogService = inject(DialogService);
  private loggingService = inject(LoggingService);
  private toastr = inject(ToastrService);

  // --- Status Dashboard ---
  readonly status = signal<SyncStatusResult | null>(null);
  readonly statusLoading = signal(false);
  readonly statusError = signal<string | null>(null);

  // --- History Table ---
  readonly history = signal<SyncHistoryResult | null>(null);
  readonly historyLoading = signal(false);
  readonly historyError = signal<string | null>(null);

  // --- Filters ---
  readonly pageIndex = signal(1);       // 1-based (server-side)
  readonly pageSize = signal(10);
  readonly dateFrom = signal<string | null>(null);
  readonly dateTo = signal<string | null>(null);
  readonly successFilter = signal<boolean | null>(null); // null=all, true=success, false=failures

  // --- Auto-refresh ---
  readonly autoRefresh = signal(true);
  private refreshInterval: ReturnType<typeof setInterval> | null = null;
  private readonly REFRESH_INTERVAL_MS = 30_000;

  // --- Action state ---
  readonly actionLoading = signal(false);
  readonly actionsCollapsed = signal(false);

  // --- Reconcile results ---
  readonly reconcileResult = signal<ReconcileSyncResult | null>(null);

  // --- Reset options ---
  readonly resetIncludeCursor = signal(false);
  readonly resetIncludeHistory = signal(false);

  // --- Computed ---
  readonly isDevEnvironment = computed(() => {
    const env = this.configService.config?.environment;
    return env === 'Local' || env === 'Dev';
  });

  readonly statusBadgeClass = computed(() => {
    switch (this.status()?.status) {
      case 'healthy': return 'label-primary';
      case 'degraded': return 'label-warning';
      case 'backlogged': return 'label-danger';
      default: return 'label-default';
    }
  });

  readonly statusBadgeText = computed(() => {
    const s = this.status()?.status;
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Unknown';
  });

  readonly paginationData = computed((): PaginationResult<SyncHistoryItem> => {
    const h = this.history();
    if (!h) {
      return {
        items: [],
        currentPage: 1,
        pageSize: this.pageSize(),
        totalItems: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
        startIndex: 0,
        endIndex: 0
      };
    }
    const totalPages = Math.ceil(h.totalCount / h.pageSize);
    const startIndex = ((h.page - 1) * h.pageSize) + 1;
    const endIndex = Math.min(h.page * h.pageSize, h.totalCount);
    return {
      items: h.items,
      currentPage: h.page,
      pageSize: h.pageSize,
      totalItems: h.totalCount,
      totalPages,
      hasNextPage: h.page < totalPages,
      hasPreviousPage: h.page > 1,
      startIndex,
      endIndex
    };
  });

  // Lifecycle
  ngOnInit(): void {
    this.loggingService.info('ImageSync', 'Initializing Image Sync component');
    this.refreshAll();
    this.startAutoRefresh();
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }

  // --- Data loading ---

  loadStatus(): void {
    this.statusLoading.set(true);
    this.statusError.set(null);

    firstValueFrom(this.imageSyncService.getStatus()).then(
      (result) => {
        this.status.set(result);
        this.statusLoading.set(false);

        if (result.status === 'degraded') {
          this.loggingService.warn('ImageSync', 'Sync status is degraded', result);
        } else if (result.status === 'backlogged') {
          this.loggingService.warn('ImageSync', 'Sync status is backlogged', result);
        }
      },
      (error) => {
        this.statusLoading.set(false);
        const message = this.extractErrorMessage(error);
        this.statusError.set(message);
        this.loggingService.error('ImageSync', 'Failed to load sync status', error);
      }
    );
  }

  loadHistory(): void {
    this.historyLoading.set(true);
    this.historyError.set(null);

    firstValueFrom(this.imageSyncService.getHistory({
      page: this.pageIndex(),
      pageSize: this.pageSize(),
      from: this.dateFrom(),
      to: this.dateTo(),
      successOnly: this.successFilter()
    })).then(
      (result) => {
        this.history.set(result);
        this.historyLoading.set(false);
      },
      (error) => {
        this.historyLoading.set(false);
        const message = this.extractErrorMessage(error);
        this.historyError.set(message);
        this.loggingService.error('ImageSync', 'Failed to load sync history', error);
      }
    );
  }

  refreshAll(): void {
    this.loadStatus();
    this.loadHistory();
  }

  // --- Auto-refresh ---

  toggleAutoRefresh(): void {
    this.autoRefresh.update(v => !v);
    if (this.autoRefresh()) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  }

  private startAutoRefresh(): void {
    this.stopAutoRefresh();
    if (this.autoRefresh()) {
      this.refreshInterval = setInterval(() => {
        this.loadStatus();
      }, this.REFRESH_INTERVAL_MS);
    }
  }

  private stopAutoRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  // --- Actions ---

  async triggerFullSync(): Promise<void> {
    const confirmed = await this.dialogService.confirm(
      'Queue a full sync of all images from Dropbox to S3?\n\nThis will re-download and upload all images.',
      { title: 'Trigger Full Sync', confirmLabel: 'Trigger Sync', cancelLabel: 'Cancel' }
    );

    if (!confirmed) return;

    this.actionLoading.set(true);
    try {
      const result = await firstValueFrom(this.imageSyncService.triggerFullSync());
      this.toastr.success(
        `Full sync triggered — ~${result.estimatedFiles} files queued`,
        'Sync Triggered'
      );
      this.loggingService.info('ImageSync', 'Full sync triggered', result);
      this.loadStatus();
    } catch (error) {
      this.toastr.error(
        `Failed to trigger sync: ${this.extractErrorMessage(error)}`,
        'Error'
      );
      this.loggingService.error('ImageSync', 'Failed to trigger full sync', error);
    } finally {
      this.actionLoading.set(false);
    }
  }

  async reconcileDryRun(): Promise<void> {
    this.actionLoading.set(true);
    this.reconcileResult.set(null);

    try {
      const result = await firstValueFrom(this.imageSyncService.reconcile(true));
      this.reconcileResult.set(result);
      this.loggingService.info('ImageSync', 'Reconcile preview completed', result);
    } catch (error) {
      this.toastr.error(
        `Failed to run reconciliation: ${this.extractErrorMessage(error)}`,
        'Error'
      );
      this.loggingService.error('ImageSync', 'Failed to reconcile (dry run)', error);
    } finally {
      this.actionLoading.set(false);
    }
  }

  async reconcileDelete(): Promise<void> {
    const orphanCount = this.reconcileResult()?.orphanedKeys?.length ?? 0;

    const confirmed = await this.dialogService.confirm(
      `Delete ${orphanCount} orphaned S3 objects? This cannot be undone.`,
      { title: 'Delete Orphans', confirmLabel: 'Delete', cancelLabel: 'Cancel' }
    );

    if (!confirmed) return;

    this.actionLoading.set(true);
    try {
      const result = await firstValueFrom(this.imageSyncService.reconcile(false));
      this.toastr.success(
        `Deleted ${result.deletedCount} orphaned objects`,
        'Reconciliation Complete'
      );
      this.loggingService.info('ImageSync', 'Reconcile delete completed', result);
      this.reconcileResult.set(null);
      this.refreshAll();
    } catch (error) {
      this.toastr.error(
        `Failed to delete orphans: ${this.extractErrorMessage(error)}`,
        'Error'
      );
      this.loggingService.error('ImageSync', 'Failed to reconcile (delete)', error);
    } finally {
      this.actionLoading.set(false);
    }
  }

  closeReconcilePanel(): void {
    this.reconcileResult.set(null);
  }

  async resetSync(): Promise<void> {
    const includeCursor = this.resetIncludeCursor();
    const includeHistory = this.resetIncludeHistory();

    if (!includeCursor && !includeHistory) {
      this.toastr.warning('Please select at least one reset option', 'No Options Selected');
      return;
    }

    const options: string[] = [];
    if (includeCursor) options.push('Dropbox sync cursor (resets sync position)');
    if (includeHistory) options.push('Sync history records');

    const confirmed = await this.dialogService.confirm(
      `This will clear the following sync state:\n\n• ${options.join('\n• ')}\n\nThis cannot be undone. Are you sure?`,
      { title: 'Reset Sync State', confirmLabel: 'Reset', cancelLabel: 'Cancel' }
    );

    if (!confirmed) return;

    this.actionLoading.set(true);
    try {
      const result = await firstValueFrom(
        this.imageSyncService.resetSync(includeCursor, includeHistory)
      );
      this.toastr.success(
        `Reset complete: ${result.outboxCleared} outbox, ${result.inboxCleared} inbox cleared`,
        'Reset Complete'
      );
      this.loggingService.info('ImageSync', 'Sync state reset', result);
      this.resetIncludeCursor.set(false);
      this.resetIncludeHistory.set(false);
      this.refreshAll();
    } catch (error) {
      this.toastr.error(
        `Failed to reset sync state: ${this.extractErrorMessage(error)}`,
        'Error'
      );
      this.loggingService.error('ImageSync', 'Failed to reset sync state', error);
    } finally {
      this.actionLoading.set(false);
    }
  }

  // --- Filter/pagination handlers ---

  onPageChange(event: PageChangeEvent): void {
    this.pageIndex.set(event.page);
    if (event.pageSize !== this.pageSize()) {
      this.pageSize.set(event.pageSize);
    }
    this.loadHistory();
  }

  onPageSizeChange(value: string): void {
    const newSize = parseInt(value, 10);
    if (newSize && newSize !== this.pageSize()) {
      this.pageSize.set(newSize);
      this.pageIndex.set(1); // Reset to first page
      this.loadHistory();
    }
  }

  onDateFilterChange(): void {
    this.pageIndex.set(1);
    this.loadHistory();
  }

  onSuccessFilterChange(value: string): void {
    if (value === 'all') {
      this.successFilter.set(null);
    } else if (value === 'success') {
      this.successFilter.set(true);
    } else {
      this.successFilter.set(false);
    }
    this.pageIndex.set(1);
    this.loadHistory();
  }

  // --- Utility ---

  formatFileSize(bytes: number | null): string {
    if (bytes === null || bytes === undefined) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  formatDuration(ms: number): string {
    if (ms < 1000) return `${ms} ms`;
    return `${(ms / 1000).toFixed(1)} s`;
  }

  formatDate(iso: string | null): string {
    if (!iso) return '—';
    try {
      // Ensure UTC interpretation — API returns UTC strings without trailing Z
      const normalized = iso.endsWith('Z') ? iso : iso + 'Z';
      const date = new Date(normalized);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch {
      return iso;
    }
  }

  getChangeTypeIcon(type: string): string {
    switch (type) {
      case 'Added': return 'fa fa-plus';
      case 'Modified': return 'fa fa-pencil';
      case 'Deleted': return 'fa fa-trash';
      default: return 'fa fa-question';
    }
  }

  getChangeTypeBadgeClass(type: string): string {
    switch (type) {
      case 'Added': return 'label-success';
      case 'Modified': return 'label-info';
      case 'Deleted': return 'label-danger';
      default: return 'label-default';
    }
  }

  private extractErrorMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === 'object' && error !== null && 'message' in error) {
      return String((error as Record<string, unknown>)['message']);
    }
    return 'An unexpected error occurred';
  }
}
